import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import get from 'lodash/get';
import type { V2DistributionPosEvolutionPeriod } from './v2.distribution.query';

// Filter options change at most daily; avoid refetching them on every remount.
export const FILTER_OPTIONS_STALE_TIME_MS = 10 * 60 * 1000;

export type GlobalDataViewFilterScope = {
  network?: 'on' | 'off';
  premium_store?: boolean;
  survey_year?: string;
};

export const useDistributionGlobalDataFilters = (
  scope?: GlobalDataViewFilterScope,
  enabled = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/global_data_view/filters',
    {
      params: scope ? { query: scope } : undefined,
      queryOpts: { enabled, staleTime: FILTER_OPTIONS_STALE_TIME_MS }
    }
  );

  return {
    isLoadingFilters: isLoading,
    refetch: refetch,
    filters: {
      zones: get(data, 'continents', []),
      wine_countries: get(data, 'wine_countries', []),
      spirit_countries: get(data, 'spirit_countries', []),
      wine_regions: get(data, 'wine_regions', []),
      wine_appelations: get(data, 'wine_appelations', []),
      wine_grape_varieties: get(data, 'wine_grape_varieties', []),
      wine_types: get(data, 'wine_types', []),
      wine_sub_types: get(data, 'wine_sub_types', []),
      wine_format_groups: get(data, 'wine_format_groups', []),
      spirit_format_groups: get(data, 'spirit_format_groups', []),
      // FIXME: Hard coded for now
      // wine_colors: get(data, 'wine_colors', []),
      surveys: get(data, 'surveys', []),
      countries: get(data, 'countries', []),
      markets: get(data, 'markets', []),
      // New store filter data
      store_price_bands: get(data, 'store_price_bands', []),
      store_facing_bands: get(data, 'store_facing_bands', []),
      store_awards: get(data, 'store_awards', []),
      store_primary_types: get(data, 'store_primary_types', []),
      store_ratings: get(data, 'store_ratings', []),
      ws_award_options: get(data, 'ws_award_options', []),
      michelin_star_options: get(data, 'michelin_star_options', []),
      vintage_options: get(data, 'vintage_options', []),
      client_refs: get(data, 'client_refs', []),
      client_rival_refs: get(data, 'client_rival_refs', {}) as Record<
        string,
        number[]
      >,
      rival_refs: get(data, 'rival_refs', []) as { id: number; name: string }[],
      client_custom_refs: get(data, 'client_custom_refs', [])
    }
  };
};

export type DistributionGlobalDataFilter = {
  currency?: 'USD' | 'EUR' | 'GBP';
  result_limit?: number;
  include_status_metrics?: boolean;
  marque_id?: number[] | null;
  client_and_rivals_only?: boolean;
  use_custom_brands?: boolean;
  network?: string | null;
  premium_store?: boolean;
  continent_id?: number | null;
  country_id?: number | null;
  market_id?: number[] | null;
  survey_year?: string | null;
  period?: V2DistributionPosEvolutionPeriod;
  wine_color_id?: number | null;
  wine_country_id?: number | null;
  wine_region_id?: number | null;
  wine_appelation_id?: number[] | null;
  wine_grape_variety_id?: number[] | null;
  wine_type_id?: number | null;
  wine_sub_type_id?: number | null;
  wine_format_group_id?: number | null;
  spirit_format_group_id?: number | null;
  // New store filter parameters
  store_type_id?: number | null;
  store_menu_price_band_id?: number[] | null;
  store_facing_band_id?: number[] | null;
  store_wine_list_size_band_ids?: number[] | null;
  store_awards_id?: number[] | null;
  wine_format_size?: string[] | null;
  store_ws_award?: string[] | null;
  store_star?: string[] | null;
  store_worlds_best?: string[] | null;
  store_top_bar?: string[] | null;
  store_spirited_award?: string[] | null;
  vintage?: string[] | null;
  store_primary_sub_type_id?: number[] | null;
  store_review_rating_id?: number[] | null;
  selected_client_ref_ids?: number[] | null;
  selected_client_custom_ids?: number[] | null;
  aggregation_type: 'wine' | 'brand' | 'point_of_sale';
  rangePrices?: [number | null, number | null] | null;
};

export const useDistributionGlobalData = (
  filterValue: DistributionGlobalDataFilter,
  enabled = true
) => {
  if (['brand', 'wine'].includes(filterValue.aggregation_type)) {
    const { isLoading, isFetching, isPlaceholderData, data, refetch } =
      useTypedQuery('get', `/global_data_view/wines`, {
        queryOpts: { enabled },
        params: {
          query: {
            ...filterValue,
            min_price: filterValue.rangePrices
              ? filterValue.rangePrices[0]
              : null,
            max_price: filterValue.rangePrices
              ? filterValue.rangePrices[1]
              : null
          }
        }
      });
    return {
      isLoadingFilters: isLoading || (isFetching && isPlaceholderData),
      refetch: refetch,
      data: get(data, 'data', [])
    };
  } else if (filterValue.aggregation_type === 'point_of_sale') {
    const { isLoading, isFetching, isPlaceholderData, data, refetch } =
      useTypedQuery('get', `/global_data_view/pos`, {
        queryOpts: { enabled },
        params: {
          query: {
            ...filterValue,
            min_price: filterValue.rangePrices
              ? filterValue.rangePrices[0]
              : null,
            max_price: filterValue.rangePrices
              ? filterValue.rangePrices[1]
              : null
          }
        }
      });
    return {
      isLoadingFilters: isLoading || (isFetching && isPlaceholderData),
      refetch: refetch,
      data: get(data, 'data', [])
    };
  } else {
    const { isLoading, isFetching, isPlaceholderData, data, refetch } =
      useTypedQuery('get', `/global_data_view/stats`, {
        queryOpts: { enabled },
        params: {
          query: {
            ...filterValue
          }
        }
      });
    return {
      isLoadingFilters: isLoading || (isFetching && isPlaceholderData),
      refetch: refetch,
      data: get(data, 'data', [])
    };
  }
};

export const useDistributionGlobalDataCount = (
  filterValue: DistributionGlobalDataFilter
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    `/global_data_view/pos/count`,
    {
      params: {
        query: {
          ...filterValue,
          min_price: filterValue.rangePrices
            ? filterValue.rangePrices[0]
            : null,
          max_price: filterValue.rangePrices ? filterValue.rangePrices[1] : null
        }
      }
    }
  );
  return {
    isLoadingFilters: isLoading,
    refetch: refetch,
    posCount: get(data, 'count', 0)
  };
};
